import { useMemo } from "react";
import cx from "classnames";

import styles from "./TableCardHead.module.scss";
import { genericMemo } from "src/utils";

// Inner imports
import type {
  TableMenuOption,
  TableItemGenericProps,
  TableProps,
} from "../../../../types";
import {
  TableItemToggler,
  TableCellMenu,
  ClickableTableCell,
  TextTableCell,
} from "../../..";

type Props<T> = {
  item: T;
  cardHeaderProp: Array<keyof T>;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  isSubTable?: boolean;
} & Pick<TableProps<T>, "tableColumns" | "getActionMenuOptions">;

export const TableCardHead = genericMemo(
  <T extends TableItemGenericProps>({
    item,
    tableColumns,
    getActionMenuOptions,
    cardHeaderProp,
    isCollapsed,
    toggleCollapse,
    isSubTable,
  }: Props<T>): JSX.Element => {
    const menuOptions = useMemo<TableMenuOption[]>(
      () => getActionMenuOptions?.(item) || [],
      [getActionMenuOptions, item],
    );

    const isCellMenuVisible = useMemo<boolean>(
      () => Boolean(menuOptions.length),
      [menuOptions.length],
    );

    const HeaderElements = useMemo<JSX.Element[]>(() => {
      const elements = cardHeaderProp
        .map((key) => {
          const column = tableColumns.find((col) => col.key === key);
          if (!column) return null;

          const { onClick, checkIsClickable } = column;
          const isClickable =
            onClick && (checkIsClickable ? checkIsClickable(item) : true);

          return isClickable ? (
            <ClickableTableCell item={item} tableColumn={column} />
          ) : (
            <TextTableCell item={item} tableColumn={column} />
          );
        })
        .filter((el): el is JSX.Element => el !== null);

      return elements.reduce((acc, current, index) => {
        acc.push(current);
        if (index < elements.length - 1) {
          acc.push(<span key={`colon-${index}`}>:</span>);
        }
        return acc;
      }, [] as JSX.Element[]);
    }, [cardHeaderProp, tableColumns, item]);

    return (
      <div
        className={cx(
          styles.tableCardHead,
          isSubTable ? styles.subTableCardHead : "",
        )}
      >
        <TableItemToggler
          isCollapsed={isCollapsed}
          toggleCollapse={toggleCollapse}
        />
        <div className={styles.tableCardHeadTitle}>{HeaderElements}</div>
        <div className={styles.tableCardHeadAction}>
          {tableColumns.map(
            ({ isAction, getComponent }) => isAction && getComponent?.(item),
          )}
          {isCellMenuVisible && (
            <TableCellMenu
              options={menuOptions}
              className={styles.tableCardHeadMenu}
            />
          )}
        </div>
      </div>
    );
  },
);
